/**
 * Calendar utility functions for appointment scheduling
 */

import type { AppointmentStatus } from '../types';

/**
 * Get status color classes for appointments
 */
export function getStatusColor(status: AppointmentStatus): {
  bg: string;
  border: string;
  text: string;
  hover: string;
} {
  switch (status) {
    case 'scheduled':
      return {
        bg: 'bg-blue-100',
        border: 'border-blue-300',
        text: 'text-blue-800',
        hover: 'hover:bg-blue-200',
      };
    case 'confirmed':
      return {
        bg: 'bg-green-100',
        border: 'border-green-300',
        text: 'text-green-800',
        hover: 'hover:bg-green-200',
      };
    case 'arrived':
      return {
        bg: 'bg-yellow-100',
        border: 'border-yellow-300',
        text: 'text-yellow-800',
        hover: 'hover:bg-yellow-200',
      };
    case 'in_progress':
      return {
        bg: 'bg-orange-100',
        border: 'border-orange-300',
        text: 'text-orange-800',
        hover: 'hover:bg-orange-200',
      };
    case 'completed':
      return {
        bg: 'bg-gray-100',
        border: 'border-gray-300',
        text: 'text-gray-600',
        hover: 'hover:bg-gray-200',
      };
    case 'cancelled':
      return {
        bg: 'bg-red-100',
        border: 'border-red-300',
        text: 'text-red-800',
        hover: 'hover:bg-red-200',
      };
    case 'no_show':
      return {
        bg: 'bg-red-200',
        border: 'border-red-400',
        text: 'text-red-900',
        hover: 'hover:bg-red-300',
      };
    default:
      return {
        bg: 'bg-gray-100',
        border: 'border-gray-300',
        text: 'text-gray-800',
        hover: 'hover:bg-gray-200',
      };
  }
}

/**
 * Get Icelandic status label
 */
export function getStatusLabel(status: AppointmentStatus): string {
  const labels: Record<AppointmentStatus, string> = {
    scheduled: 'Áætlaður',
    confirmed: 'Staðfestur',
    arrived: 'Mættur',
    in_progress: 'Í gangi',
    completed: 'Lokið',
    cancelled: 'Afbókað',
    no_show: 'Mætti ekki',
  };
  return labels[status];
}

/**
 * Format time in Icelandic format (HH:MM)
 */
export function formatTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('is-IS', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format date in Icelandic format
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('is-IS', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format date short (dd.mm.yyyy)
 */
export function formatDateShort(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('is-IS');
}

/**
 * Get week start (Monday) for a given date
 */
export function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
}

/**
 * Get week end (Friday) for a given date
 */
export function getWeekEnd(date: Date): Date {
  const weekStart = getWeekStart(date);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 4); // Friday (Mon + 4 days)
  return weekEnd;
}

/**
 * Get array of dates for a week (Mon-Fri)
 */
export function getWeekDays(weekStart: Date): Date[] {
  const days: Date[] = [];
  for (let i = 0; i < 5; i++) {
    const day = new Date(weekStart);
    day.setDate(day.getDate() + i);
    days.push(day);
  }
  return days;
}

/**
 * Get calendar grid for a month (6 weeks)
 */
export function getMonthCalendar(year: number, month: number): Date[][] {
  const firstDay = new Date(year, month, 1);

  // Get the Monday of the week containing the first day
  const calendarStart = getWeekStart(firstDay);

  const weeks: Date[][] = [];
  let currentDate = new Date(calendarStart);

  // Generate 6 weeks to cover all possible month layouts
  for (let week = 0; week < 6; week++) {
    const weekDays: Date[] = [];
    for (let day = 0; day < 7; day++) {
      weekDays.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    weeks.push(weekDays);
  }

  return weeks;
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Check if date is today
 */
export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

/**
 * Generate time slots for a day (8:00-17:00, 15-minute intervals)
 */
export function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let hour = 8; hour < 17; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      slots.push(`${h}:${m}`);
    }
  }
  return slots;
}

/**
 * Convert time string to minutes since midnight
 */
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Convert minutes since midnight to time string
 */
export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Check if time is within office hours (8:00-17:00)
 */
export function isWithinOfficeHours(time: string): boolean {
  const minutes = timeToMinutes(time);
  return minutes >= 8 * 60 && minutes < 17 * 60;
}

/**
 * Get Icelandic month name
 */
export function getIcelandicMonthName(month: number): string {
  const months = [
    'janúar',
    'febrúar',
    'mars',
    'apríl',
    'maí',
    'júní',
    'júlí',
    'ágúst',
    'september',
    'október',
    'nóvember',
    'desember',
  ];
  return months[month];
}

/**
 * Get Icelandic weekday name
 */
export function getIcelandicWeekdayName(day: number): string {
  const days = [
    'sunnudagur',
    'mánudagur',
    'þriðjudagur',
    'miðvikudagur',
    'fimmtudagur',
    'föstudagur',
    'laugardagur',
  ];
  return days[day];
}

/**
 * Get short Icelandic weekday name
 */
export function getIcelandicWeekdayShort(day: number): string {
  const days = ['Sun', 'Mán', 'Þri', 'Mið', 'Fim', 'Fös', 'Lau'];
  return days[day];
}

/**
 * Format duration in Icelandic
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} mín`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours} klst`;
  }
  return `${hours} klst ${mins} mín`;
}

/**
 * Parse ISO datetime string to local Date
 */
export function parseISODateTime(isoString: string): Date {
  return new Date(isoString);
}

/**
 * Format Date to ISO datetime string for API
 */
export function toISODateTime(date: Date): string {
  return date.toISOString();
}

/**
 * Combine date and time strings into ISO datetime
 */
export function combineDateAndTime(date: string, time: string): string {
  return `${date}T${time}:00.000Z`;
}

/**
 * Get time from ISO datetime string
 */
export function getTimeFromISO(isoString: string): string {
  const date = new Date(isoString);
  return formatTime(date);
}

/**
 * Get date from ISO datetime string (YYYY-MM-DD)
 */
export function getDateFromISO(isoString: string): string {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}
